import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, userAPI } from '../services/api';
import Loading from '../components/Loading';
import StitchHeader from '../components/StitchHeader';
import UserAvatar from '../components/UserAvatar';
import '../styles/Profile.css';

const MAX_AVATAR_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

function splitName(name = '') {
  const [firstName = '', ...lastNameParts] = name.trim().split(/\s+/).filter(Boolean);
  return { firstName, lastName: lastNameParts.join(' ') };
}

function Profile() {
  const navigate = useNavigate();
  const { user, updateUser, login } = useAuth();
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [firstName, setFirstName] = useState(() => splitName(user?.name).firstName);
  const [lastName, setLastName] = useState(() => splitName(user?.name).lastName);
  const [phone, setPhone] = useState(user?.phone || '');
  const [phoneCode, setPhoneCode] = useState('');
  const [pendingPhone, setPendingPhone] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let isActive = true;

    userAPI.getProfile()
      .then((response) => {
        const profile = response.data?.user;
        if (!isActive || !profile) return;

        const profileName = splitName(profile.name);
        setFirstName(profileName.firstName);
        setLastName(profileName.lastName);
        setPhone(profile.phone || '');
        updateUser({ ...user, ...profile });
      })
      .catch(() => {});

    return () => {
      isActive = false;
    };
  }, []);

  const handleProfileSave = async (event) => {
    event.preventDefault();
    const name = `${firstName.trim()} ${lastName.trim()}`.trim();
    if (!name) {
      setError('نام را وارد کنید.');
      return;
    }

    setSavingProfile(true);
    setMessage('');
    setError('');
    try {
      if (user?.auth_provider === 'bale' && phone.trim() && phone.trim() !== user.phone) {
        const response = await authAPI.sendOTP(phone.trim());
        if (!response.data?.success) throw new Error(response.data?.error || 'ارسال کد ناموفق بود.');

        setPendingPhone(phone.trim());
        setMessage('کد تأیید ارسال شد. کد را وارد کنید تا حساب بله به همین شماره متصل شود.');
        return;
      }

      const response = await userAPI.updateProfile({ name, phone: phone.trim() });
      if (!response.data?.success) throw new Error(response.data?.error || 'ذخیره پروفایل ناموفق بود.');

      updateUser({ ...user, name, phone: phone.trim() || null });
      setMessage('اطلاعات پروفایل ذخیره شد.');
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError?.message || 'خطا در ذخیره اطلاعات پروفایل.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleBalePhoneVerification = async (event) => {
    event.preventDefault();
    if (phoneCode.length !== 6) {
      setError('کد تأیید باید ۶ رقم باشد.');
      return;
    }

    setSavingProfile(true);
    setError('');
    try {
      const response = await userAPI.linkBalePhone(pendingPhone, phoneCode);
      if (!response.data?.success) throw new Error(response.data?.error || 'اتصال حساب ناموفق بود.');

      login(response.data.token, { ...response.data.user, auth_provider: 'bale' });
      setPhone(response.data.user.phone || pendingPhone);
      setPendingPhone('');
      setPhoneCode('');
      setMessage('حساب بله و حساب موبایلی شما یکپارچه شد.');
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError?.message || 'خطا در اتصال حساب.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAvatarChange = async (event) => {
    const [file] = event.target.files || [];
    event.target.value = '';
    if (!file) return;

    setMessage('');
    setError('');
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('لطفاً یک تصویر JPG، PNG یا WEBP انتخاب کنید.');
      return;
    }
    if (file.size > MAX_AVATAR_SIZE) {
      setError('حجم عکس باید حداکثر ۵ مگابایت باشد.');
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);
    setUploading(true);

    try {
      const response = await userAPI.uploadAvatar(file);
      if (!response.data?.success) throw new Error(response.data?.error || 'آپلود عکس ناموفق بود.');

      const updatedUser = response.data.user || {
        ...user,
        avatar_url: response.data.avatar_url || response.data.profile_image
      };
      updateUser({ ...user, ...updatedUser });
      setPreviewUrl(null);
      URL.revokeObjectURL(localPreview);
      setMessage('عکس پروفایل با موفقیت به‌روزرسانی شد.');
    } catch (requestError) {
      setPreviewUrl(null);
      URL.revokeObjectURL(localPreview);
      setError(requestError?.error || requestError?.message || 'خطا در آپلود عکس پروفایل.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="profile-page">
      <StitchHeader />
      <main className="profile-main">
        <section className="profile-card">
          <button className="profile-back material-symbols-outlined" onClick={() => navigate(-1)} type="button" aria-label="بازگشت">arrow_forward</button>
          <div className="profile-heading"><h1>پروفایل من</h1><p>عکس خودت را انتخاب کن تا در تمام بخش‌های بازی نمایش داده شود.</p></div>
          <div className="profile-avatar-editor">
            <UserAvatar user={user} avatarUrl={previewUrl || user?.avatar_url} className="profile-avatar" />
            <button className="profile-avatar-edit material-symbols-outlined" onClick={() => inputRef.current?.click()} disabled={uploading} type="button" aria-label="تغییر عکس پروفایل">photo_camera</button>
          </div>
          <input ref={inputRef} className="profile-file-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleAvatarChange} />
          <h2>{user?.name || 'کاربر'}</h2>
          <form className="profile-details-form" onSubmit={handleProfileSave}>
            <div className="profile-name-fields">
              <label>
                <span>نام</span>
                <input value={firstName} onChange={(event) => setFirstName(event.target.value)} maxLength="50" autoComplete="given-name" />
              </label>
              <label>
                <span>نام خانوادگی</span>
                <input value={lastName} onChange={(event) => setLastName(event.target.value)} maxLength="50" autoComplete="family-name" />
              </label>
            </div>
            <label>
              <span>شماره موبایل</span>
              <input value={phone} onChange={(event) => setPhone(event.target.value)} inputMode="tel" placeholder="09123456789" autoComplete="tel" />
            </label>
            <p>عکس بله در ورود اول ذخیره می‌شود. نام و شماره را خودتان تأیید یا اصلاح کنید.</p>
            <button className="profile-save-button" type="submit" disabled={savingProfile}>
              {savingProfile ? 'در حال ذخیره...' : 'ذخیره اطلاعات'}
            </button>
          </form>
          {pendingPhone && (
            <form className="profile-details-form" onSubmit={handleBalePhoneVerification}>
              <label>
                <span>کد تأیید ارسال‌شده به {pendingPhone}</span>
                <input value={phoneCode} onChange={(event) => setPhoneCode(event.target.value.replace(/\D/g, ''))} inputMode="numeric" maxLength="6" autoFocus />
              </label>
              <button className="profile-save-button" type="submit" disabled={savingProfile}>تأیید و اتصال حساب</button>
            </form>
          )}
          <button className="profile-upload-button" onClick={() => inputRef.current?.click()} disabled={uploading} type="button"><span className="material-symbols-outlined">upload</span>{uploading ? 'در حال آپلود...' : 'انتخاب عکس پروفایل'}</button>
          <p className="profile-help">فرمت‌های مجاز: JPG، PNG و WEBP — حداکثر ۵ مگابایت</p>
          {message && <p className="profile-feedback success">{message}</p>}
          {error && <p className="profile-feedback error">{error}</p>}
        </section>
      </main>
      {uploading && <div className="profile-upload-overlay"><Loading message="در حال ذخیره عکس..." /></div>}
    </div>
  );
}

export default Profile;
